import {
  Button,
  cn,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
  ScrollArea,
  VisuallyHidden,
} from "web-design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { VisibilityState } from "@tanstack/react-table";
import { EditColumns, EditFieldsProps, TableOfContents } from "./edit-fields";

interface EditColumnsDialogProps extends EditFieldsProps {
  label: string;
  header: string;
}

export function EditColumnsDialog(props: EditColumnsDialogProps) {
  const { columnsConfig, onReorder, onVisibilityChange } = props;

  const [activeSection, setActiveSection] = useState<string>("visible-metrics");
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const [localColumnConfig, setLocalColumnConfig] = useState(columnsConfig);

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setLocalColumnConfig(columnsConfig);
  }, [columnsConfig]);

  const handleVisibilityChange = (changes: VisibilityState) => {
    setLocalColumnConfig((prev) =>
      prev.map((p) => (p.id in changes ? { ...p, visible: changes[p.id] } : p))
    );
  };

  const handleReorder = (order: Array<string>) => {
    setLocalColumnConfig((prev) =>
      prev.toSorted((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    );
  };

  const columns = useMemo(
    () =>
      Array.from(
        new Set(
          columnsConfig.map(
            (column) => column.group?.toLocaleLowerCase() as string
          )
        )
      ),
    [columnsConfig]
  );

  const handleScroll = useCallback(() => {
    const columnIds = ["visible-metrics", ...columns];
    const rect = scrollAreaRef.current?.getBoundingClientRect();
    if (!rect) return;
    const distanceFromTop = columnIds.map((c) => {
      const ele = document.getElementById(c);
      if (!ele) return Infinity;
      const rectEle = ele.getBoundingClientRect();
      return Math.abs(rectEle.top - rect.top);
    });
    const max = Math.min(...distanceFromTop);
    setActiveSection(columnIds[distanceFromTop.findIndex((m) => m === max)]);
  }, [columns]);

  const applyChanges = useCallback(() => {
    onReorder?.(localColumnConfig.map((lc) => lc.id));
    onVisibilityChange?.(
      localColumnConfig.reduce(
        (acc, lc) => ({
          ...acc,
          [lc.id]: lc.visible,
        }),
        {}
      )
    );
    setOpen(false);
  }, [onReorder, onVisibilityChange, localColumnConfig]);

  const discardChanges = useCallback(() => {
    setLocalColumnConfig(columnsConfig);
    setOpen(false);
  }, [columnsConfig]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger className="w-full" asChild>
        <Button onClick={() => setOpen(true)} variant="outline">
          {props.label}
        </Button>
      </DialogTrigger>
      <DialogContent className="h-140 w-140 max-w-140 p-0">
        <VisuallyHidden>
          <DialogTitle>{props.header}</DialogTitle>
          <DialogDescription>Dialog to edit fields</DialogDescription>
        </VisuallyHidden>
        <div className="flex flex-row size-full rounded-lg overflow-hidden">
          <TableOfContents {...props} activeSection={activeSection} />
          <ScrollArea
            ref={scrollAreaRef}
            onScroll={handleScroll}
            className="flex-4 relative min-w-80 p-6"
          >
            <div className="pb-100">
              <EditColumns
                {...props}
                columnsConfig={localColumnConfig}
                onReorder={handleReorder}
                onVisibilityChange={handleVisibilityChange}
              />
            </div>
            <div
              className={cn(
                "absolute bg-background border-t border-border flex items-center gap-2 bottom-0 p-5 left-0 right-0"
              )}
            >
              <Button
                onClick={discardChanges}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button onClick={applyChanges} className="flex-1">
                Apply
              </Button>
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}
